import { useQuery } from '@tanstack/react-query';
import { Link, Outlet } from '@tanstack/react-router';
import { useApi, useSession } from '../lib/api';
import { navSections, pages } from '../nav';
import { LoginPage } from '../pages/LoginPage';

/** Sign-in gate, then the sidebar shell. The API enforces every permission; this only picks the screen. */
export function Layout() {
  const { client, session } = useApi();
  const tokens = useSession();

  // Probe an admin endpoint: 403 for staff means the session still needs its TOTP step.
  const probe = useQuery({
    queryKey: ['admin-probe', tokens?.accessToken],
    queryFn: async () => (await client.GET('/admin/stats')).response.status,
    enabled: !!tokens && session.isStaff,
    staleTime: Infinity,
  });

  if (!tokens) return <LoginPage />;

  if (!session.isStaff) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4">
        <p className="text-lg">{tokens.userId} isn&rsquo;t on the staff list.</p>
        <button
          type="button"
          onClick={() => session.set(null)}
          className="rounded border-2 border-ink px-3 py-1 font-semibold"
        >
          Sign out
        </button>
      </div>
    );
  }

  if (probe.data === 403 && !tokens.mfa) return <LoginPage needsMfa />;

  return (
    <div className="flex min-h-dvh">
      <aside className="flex w-60 shrink-0 flex-col bg-ink text-newsprint">
        <Link
          to="/"
          className="flex items-center gap-2 border-b border-bruise px-4 py-4"
          aria-label="ARG Zombie Admin home"
        >
          <span className="inline-block -rotate-3 bg-caption px-2 py-0.5 font-bang text-2xl tracking-wide text-ink">
            ARG Zombie
          </span>
          <span className="text-xs tracking-widest text-nonphoto uppercase">Admin</span>
        </Link>
        <nav aria-label="Admin" className="flex-1 overflow-y-auto px-2 py-4 text-sm">
          {navSections.map((s) => (
            <div key={s.label ?? 'top'} className="mb-4">
              {s.label && (
                <p className="px-2 pb-1 text-xs font-semibold tracking-widest text-nonphoto uppercase">
                  {s.label}
                </p>
              )}
              <ul>
                {s.items.map((key) => (
                  <li key={key}>
                    <Link
                      to={pages[key].to}
                      activeOptions={{ exact: true }}
                      className="block rounded px-2 py-1 hover:bg-bruise"
                      activeProps={{ className: 'bg-benday text-ink font-semibold' }}
                    >
                      {pages[key].title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="border-t border-bruise px-4 py-3 text-xs">
          <p className="text-nonphoto">
            {tokens.roles.join(', ')}
            {tokens.mfa ? ' · MFA' : ''}
          </p>
          <button type="button" onClick={() => session.set(null)} className="mt-1 underline">
            Sign out
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-8">
        <Outlet />
      </main>
    </div>
  );
}
